// src/components/Login.js
import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  TextField, Button, Box, Typography, Link, CircularProgress
} from '@mui/material';
import { styled } from '@mui/system';
import { jwtDecode } from 'jwt-decode';
import { AuthContext } from '../context/AuthContext';
import ForgotPasswordDialog from './ForgotPassword';
import { useLocation } from 'react-router-dom';

const Root = styled(Box)({
  height: '100vh',
  display: 'flex',
  backgroundColor: 'black',
});

const LeftSide = styled(Box)({
  flex: 7,
  backgroundColor: '#282c34',
  color: 'white',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '40px',
  textAlign: 'center',
});

const RightSide = styled(Box)({
  flex: 3,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'white',
  padding: '20px',
});

const FormContainer = styled(Box)({
  width: '100%',
  maxWidth: '400px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
});

const ErrorText = styled(Typography)({
  color: 'red',
  marginBottom: '10px',
});

const SignUpLabel = styled(Typography)({
  marginTop: '10px',
  color: '#1976d2',
  cursor: 'pointer',
});

const Login = () => {
  const location = useLocation();
  const from = location.state?.from || { pathname: '/home' };

  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { login, setIsLoggingOut } = useContext(AuthContext);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/login`, {
        email,
        password,
      });
      const token = response.data.token;
      login(token);
      setIsLoggingOut(false);
      localStorage.setItem('token', token);
      const decoded = jwtDecode(token);
      localStorage.setItem('accessLevel', decoded.accessLevel);
      navigate(from, { replace: true });
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/signup`, {
        email,
        firstName,
        lastName,
        mobile,
      });

      if (response.data.success) {
        alert('Signup complete pending for approval!');
        setIsSignUp(false);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Error during signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleForm = () => {
    setIsSignUp(!isSignUp);
    setError('');
  };

  return (
    <Root>
      {/* <LeftSide>
      <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
          Welcome to Udyog Connect
        </Typography>
        <Typography variant="h6" sx={{ maxWidth: '80%', lineHeight: 1.6 }}>
          Empowering communities by connecting people who need help with those who can offer it.
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: '90%', mt: 3 }}>
          Join our growing community of professionals and make a difference by sharing your expertise or receiving guidance in areas that matter to you. Whether you're here to lend a helping hand or seek support, Udyog Connect provides the platform to make meaningful connections.
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: '90%', mt: 2 }}>
          Together, we can create an ecosystem of collaboration and growth. Sign up today and be part of the change!
        </Typography>
      </LeftSide> */}
      <RightSide>
        <FormContainer>
          <Typography variant="h5" sx={{ marginBottom: '20px', fontWeight: '600' }}>
            {isSignUp ? 'Sign Up' : 'Login'}
          </Typography>

          <form onSubmit={isSignUp ? handleSignUp : handleLogin}>
            {isSignUp && (
              <>
                <TextField
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Mobile"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                />
              </>
            )}

            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            />

            {!isSignUp && (
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                sx={{ mb: 2 }}
              />
            )}
            {error && <ErrorText>{error}</ErrorText>}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : isSignUp ? 'Sign Up' : 'Login'}
            </Button>          
          </form>

          <SignUpLabel>
            {isSignUp ? (
              <>
                Already have an account?{' '}
                <Link onClick={toggleForm} underline="hover" sx={{ cursor: 'pointer' }}>
                  Login
                </Link>
              </>
            ) : (
              <>
                Don’t have an account?{' '}
                <Link onClick={toggleForm} underline="hover" sx={{ cursor: 'pointer' }}>
                  Sign Up
                </Link> 
                <br/>
                {!isSignUp && (
                <Link
                    component="button"
                    variant="body2"
                    onClick={() => setShowForgotPassword(true)}
                    sx={{ cursor: 'pointer' }}
                  >
                    Forgot Password?
                </Link>
            )}
              </>
            )}
          </SignUpLabel>
        </FormContainer>
        <ForgotPasswordDialog
          open={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
        />
      </RightSide>
    </Root>
  );
};

export default Login;
